import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";

import { useAuth } from "@/lib/auth-context";
import { normalizeImg } from "@/lib/images";
import { getMarketplaceCatalog } from "@/lib/coreApi";

const CORE_API = import.meta.env.VITE_CORE_API_URL || '';

interface MarketItem {
  id: string;
  sku?: string | null;
  kind: string;
  title: string;
  description?: string | null;
  preview_url?: string | null;
  price_credits: number | null;
  price_cents?: number | null;
  meta?: Record<string, any> | null;
}

export default function SponsorMarketplacePage() {
  const { session } = useAuth();
  const token = session?.access_token;

  const [items, setItems] = useState<MarketItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyItem, setBusyItem] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    async function load() {
      setLoading(true);
      setError(null);
      try {
        const catalogRes = await getMarketplaceCatalog("sponsor");
        if (cancelled) return;
        setItems(Array.isArray(catalogRes?.data?.items) ? catalogRes.data.items as MarketItem[] : []);
      } catch (err: any) {
        if (cancelled) return;
        setError(err?.message || "Could not load marketplace.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    }
    load();
    return () => {
      cancelled = true;
    };
  }, [token]);

  const categories = useMemo(() => {
    const specialOffers = items.filter((i) => i.kind === "special_offer");
    const creditPacks = items.filter((i) => i.kind === "credit_pack");
    const cosmetics = items.filter((i) =>
      ["badge", "avatar", "theme", "border", "digital_good"].includes(i.kind)
    );

    return { specialOffers, creditPacks, cosmetics };
  }, [items]);

  async function handleSpend(item: MarketItem) {
    setBusyItem(item.id);
    setError(null);
    try {
      if (!token) throw new Error("You must be signed in to purchase items");

      const res = await fetch(`${CORE_API}/api/v1/marketplace/purchase`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`,
        },
        body: JSON.stringify({ item_id: item.id }),
      });
      const json = await res.json();
      if (!res.ok) throw new Error(json?.error?.message || json?.error || "Purchase failed");
    } catch (err: any) {
      setError(err?.message || "Purchase failed");
    } finally {
      setBusyItem(null);
    }
  }

  function ItemCard({ item }: { item: MarketItem }) {
    const soldOut = item.kind === "special_offer" && (item.meta?.quantity_remaining ?? 0) <= 0;

    return (
      <div className="flex flex-col gap-2 rounded-lg border bg-white p-3 text-sm shadow-sm">
        {item.preview_url && (
          <img
            src={normalizeImg(item.preview_url)}
            alt={item.title}
            className="h-24 w-full rounded object-cover"
          />
        )}
        <div className="font-medium text-slate-900">{item.title}</div>
        {item.description && (
          <p className="text-xs text-slate-600 line-clamp-3">{item.description}</p>
        )}
        <div className="mt-auto space-y-1 text-[11px] text-slate-500">
          <div>Price: {item.price_credits ?? 0} credits</div>
          {item.kind === "special_offer" && (
            <div>Remaining: {item.meta?.quantity_remaining ?? "?"}</div>
          )}
          {item.meta?.cash_value_cents != null && (
            <div>Cash value: ${(item.meta.cash_value_cents / 100).toFixed(2)}</div>
          )}
        </div>
        <button
          type="button"
          disabled={busyItem === item.id || soldOut}
          onClick={() => handleSpend(item)}
          className="mt-2 rounded border px-2 py-1 text-xs disabled:opacity-60"
        >
          {soldOut
            ? "Sold out"
            : busyItem === item.id
            ? "Processing…"
            : item.kind === "special_offer"
            ? `Claim for ${item.price_credits ?? 0} credits`
            : "Buy"}
        </button>
      </div>
    );
  }

  if (loading) {
    return <div className="p-4 text-sm text-slate-500">Loading marketplace…</div>;
  }

  if (error) {
    return <div className="p-4 text-sm text-red-600">{error}</div>;
  }

  if (!items.length) {
    return (
      <div className="p-4 text-sm text-slate-500">
        No marketplace items are available right now.
      </div>
    );
  }

  return (
    <div className="space-y-8 p-4">
      <div className="rounded-lg bg-sand/60 p-4 text-xs text-cocoa">
        <p className="font-semibold">Support teachers with credits and gifts</p>
        <p className="mt-1 text-cocoa/80">
          Purchase teacher gift credit packs or classroom marketplace items here. All purchases run through Tower credits and
          ledgered grants—no USD is held for teachers directly.
        </p>
      </div>
      {categories.specialOffers.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Special offers</h2>
          <p className="mb-3 text-xs text-slate-500">
            First come, first served. Claim using your Tower Credits.
          </p>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {categories.specialOffers.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

      {/* Credit packs are purchased via Stripe on the Wallet page */}
      <section>
        <h2 className="mb-2 text-sm font-semibold">Credit packs</h2>
        <p className="text-xs text-cocoa/70 mb-2">
          Credit packs are purchased with real money on the wallet page.
        </p>
        <Link
          to="/sponsor/wallet"
          className="inline-block rounded-lg bg-gold px-4 py-2 text-sm font-semibold text-cocoa hover:opacity-90"
        >
          Go to Wallet to buy credits
        </Link>
      </section>

      {categories.cosmetics.length > 0 && (
        <section>
          <h2 className="mb-2 text-sm font-semibold">Decorate my sponsor page</h2>
          <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 md:grid-cols-3">
            {categories.cosmetics.map((item) => (
              <ItemCard key={item.id} item={item} />
            ))}
          </div>
        </section>
      )}

    </div>
  );
}
